import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { type ApiError, api, type SignUpResult } from '@whinerp/client';
import { Button, Field, FormMessage, Icon, SerifMoment, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-01 Signup.
 *
 * One centred card, no navigation chrome, and one decision per screen: the
 * sign-in link is the default path because it removes password creation from
 * the very first thing somebody does. The password field appears only if they
 * ask for it.
 *
 * Provisioning shows named steps, never a bare spinner. The steps are what the
 * server actually does, in the order it does them, inside one transaction -
 * they are reported rather than animated, and if it fails there is no partial
 * workspace to explain.
 */

const PROVISION_STEP_KEYS = [
  'auth.signup.step.tenant',
  'auth.signup.step.roles',
  'auth.signup.step.schema',
  'auth.signup.step.modules',
  'auth.signup.step.ready',
] as const;

function SignupScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [usePassword, setUsePassword] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [duplicate, setDuplicate] = useState(false);

  const signUp = useMutation<SignUpResult, ApiError>({
    mutationFn: () =>
      api.signUp({
        body: {
          workspaceName,
          email,
          ...(usePassword ? { password } : {}),
        },
      }),
    onSuccess: () => navigate('/onboarding'),
    onError: (error) => setDuplicate(error.code === 'CONFLICT'),
  });

  const fieldError = (field: string): string | undefined =>
    signUp.error?.problems?.find((problem) => problem.field === field)?.message;

  // A failure that any field already owns is reported on that field and
  // nowhere else. Showing it twice makes a screen reader announce the same
  // problem twice and makes a sighted person hunt for a second thing to fix.
  const hasFieldError = (signUp.error?.problems ?? []).length > 0;

  if (signUp.isPending) return <Provisioning />;

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-5">
      <div
        data-component="SignupCard"
        // The design system's narrow-overlay width: a signup card and a confirm
        // dialog are the same shape of thing, so they are the same width.
        className="flex w-full max-w-modal-confirm flex-col gap-5 rounded-card bg-surface p-6"
      >
        <div className="flex flex-col gap-2">
          <Icon name="Diamond" decorative className="text-bronze" />
          <SerifMoment>{t('auth.signup.title')}</SerifMoment>
          <p className="text-body text-ink-meta">{t('auth.signup.subtitle')}</p>
        </div>

        <form
          data-component="SignupForm"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setDuplicate(false);
            signUp.mutate();
          }}
        >
          <Field
            label={t('auth.signup.orgName')}
            hint={t('auth.signup.orgNameHint')}
            value={workspaceName}
            onChange={(event) => setWorkspaceName(event.target.value)}
            autoComplete="organization"
            required
            {...(fieldError('workspaceName') ? { error: fieldError('workspaceName') } : {})}
          />

          <Field
            label={t('auth.signup.email')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
            {...(fieldError('email') ? { error: fieldError('email') } : {})}
          />

          {/* One decision per screen: the password field is not shown until it
              is asked for, so the default path has two fields and no choices. */}
          {usePassword ? (
            <Field
              label={t('auth.signup.password')}
              hint={t('auth.signup.passwordHint')}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
              {...(fieldError('password') ? { error: fieldError('password') } : {})}
            />
          ) : null}

          {duplicate ? (
            <div className="flex flex-col items-start gap-2">
              <FormMessage tone="error">
                {t('auth.signup.duplicateEmail', { values: { email: email || '—' } })}
              </FormMessage>
              <Link
                to="/sign-in"
                className="text-body font-medium text-bronze-text underline decoration-bronze-rule underline-offset-4"
              >
                {t('auth.signup.duplicateEmailAction')}
              </Link>
            </div>
          ) : signUp.isError && !hasFieldError ? (
            <FormMessage tone="error">{signUp.error.message}</FormMessage>
          ) : null}

          <Button type="submit" intent="primary" busy={signUp.isPending}>
            {t('auth.signup.submit')}
          </Button>

          <button
            type="button"
            onClick={() => setUsePassword((value) => !value)}
            className="self-start text-dense text-bronze-text underline decoration-bronze-rule underline-offset-4"
          >
            {usePassword ? t('auth.signup.useMagicLink') : t('auth.signup.usePassword')}
          </button>
        </form>
      </div>
    </main>
  );
}

/**
 * The provisioning state.
 *
 * Named steps with useful copy, because provisioning can take a few seconds and
 * a person waiting deserves to know what is being built rather than watching a
 * circle. The steps are not a progress bar: they describe the work, and the
 * work either completes or leaves nothing behind.
 */
function Provisioning(): JSX.Element {
  const t = useT();
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-5">
      <div
        data-component="ProvisioningProgress"
        role="status"
        aria-live="polite"
        aria-busy
        className="flex w-full max-w-modal-confirm flex-col gap-4 rounded-card bg-surface p-6"
      >
        <SerifMoment as="h1">{t('auth.signup.provisioning')}</SerifMoment>
        <ul className="flex flex-col gap-3">
          {PROVISION_STEP_KEYS.map((key) => (
            <li key={key} className="flex items-center gap-3 text-body text-ink-600">
              <Skeleton className="h-icon w-icon" rounded="chip" />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        <p className="text-meta text-ink-meta">{t('auth.signup.slow')}</p>
      </div>
    </main>
  );
}

export const screen = defineScreen({
  id: 'SC-01',
  route: '/sign-up',
  surface: 'page',
  title: 'Signup',
  features: ['F-001'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'unauthenticated' }],
  components: [],
  elements: ['SignupCard', 'SignupForm'],
  states: {
    // A signup form is never a list of nothing.
    empty: { notApplicable: 'Signup is a form, not a collection: there is nothing for it to be empty of.' },
    loading: Provisioning,
    // Failures land on the fields that caused them, or above the form when no
    // single field is at fault. A page-level error state would replace the form
    // and lose what the person had typed.
    error: {
      notApplicable:
        'Errors render inline on the form, keeping every value the person entered. A page-level error would discard them.',
    },
  },
  component: SignupScreen,
});

export default screen;
